import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Typography,
  Tag,
  Button,
  Space,
  Row,
  Col,
  Timeline,
  Statistic,
  Table,
  Avatar,
  Image,
  Spin,
  message,
  Empty
} from 'antd';
import {
  UserOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  SwapOutlined
} from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';

const { Text, Title } = Typography;
const API = 'https://limitverify.ru:5000';

const ACTION_LABELS = {
  created: 'Создание изделия',
  updated: 'Обновление',
  transferred: 'Передача владения',
  verified: 'Верификация',
  assigned: 'Назначение владельца',
  status_changed: 'Смена статуса',
  photo_added: 'Добавлено фото',
  deleted: 'Удаление'
};

const ProductCard = () => {
  const [product, setProduct] = useState(null);
  const [owners, setOwners] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const token = localStorage.getItem('token');
  const fromTicketId = location.state?.fromTicketId;
  const authHeaders = { Authorization: `Bearer ${token}` };

  const goBack = () => {
    if (fromTicketId) {
      navigate(`/tickets/${fromTicketId}`);
      return;
    }
    navigate(-1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const loadAll = useCallback(async () => {
    if (!id || !token) return;
    setLoading(true);
    try {
      const [productRes, ownersRes, historyRes] = await Promise.all([
        axios.get(`${API}/api/products/${id}`, { headers: authHeaders }),
        axios.get(`${API}/api/product-history/product/${id}/owners`, { headers: authHeaders }),
        axios.get(`${API}/api/product-history/product/${id}/history`, { headers: authHeaders })
      ]);

      setProduct(productRes.data);

      const ownersData = ownersRes.data?.data || [];
      setOwners(
        ownersData.map((o) => ({
          id: o.id || o.clientId,
          clientId: o.clientId || o.client?.id,
          name:
            o.client?.fullName ||
            `${o.client?.firstName || ''} ${o.client?.lastName || ''}`.trim() ||
            'Неизвестный',
          email: o.client?.email || '—',
          phone: o.client?.phone || '—',
          start_date: o.ownershipStartDate,
          end_date: o.ownershipEndDate,
          is_current: o.status === 'current' || !o.ownershipEndDate
        }))
      );

      const historyData = historyRes.data?.data || [];
      setHistory(
        historyData.map((h) => {
          const userName = h.user
            ? `${h.user.firstName || ''} ${h.user.lastName || ''}`.trim() || h.user.email
            : null;
          const clientName = h.client
            ? `${h.client.firstName || ''} ${h.client.lastName || ''}`.trim() || h.client.email
            : null;
          return {
            id: h.id,
            action: ACTION_LABELS[h.actionType] || h.actionType || 'Событие',
            date: h.actionDate || h.action_date || h.createdAt,
            description: h.description || '—',
            user: userName || clientName || 'Система'
          };
        })
      );
    } catch (error) {
      console.error('Ошибка загрузки карточки изделия:', error);
      if (error.response?.status === 404) {
        setProduct(null);
      } else {
        message.error('Не удалось загрузить карточку изделия');
      }
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const getStatusConfig = (status) => {
    const configs = {
      АКТИВНО: { color: 'green', icon: <CheckCircleOutlined />, text: 'Активно' },
      АРХИВ: { color: 'default', icon: <ClockCircleOutlined />, text: 'Архив' },
      active: { color: 'green', icon: <CheckCircleOutlined />, text: 'Активно' },
      archived: { color: 'default', icon: <ClockCircleOutlined />, text: 'Архив' },
      verified: { color: 'green', icon: <CheckCircleOutlined />, text: 'Проверено' },
      pending: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Ожидает' },
      rejected: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Отклонено' },
      transferred: { color: 'blue', icon: <SwapOutlined />, text: 'Передано' }
    };
    return configs[status] || { color: 'default', icon: <ClockCircleOutlined />, text: status || '—' };
  };

  const ownerColumns = [
    {
      title: 'Владелец',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{text}</div>
            <div style={{ fontSize: 12, color: '#666' }}>{record.email}</div>
          </div>
        </Space>
      )
    },
    {
      title: 'Период владения',
      key: 'period',
      render: (_, record) => (
        <div>
          <div style={{ fontSize: 12 }}>С: {formatDate(record.start_date)}</div>
          <div style={{ fontSize: 12 }}>
            По: {record.end_date ? formatDate(record.end_date) : 'Настоящее время'}
          </div>
        </div>
      )
    },
    {
      title: 'Статус',
      dataIndex: 'is_current',
      key: 'is_current',
      render: (isCurrent) => (
        <Tag color={isCurrent ? 'green' : 'default'}>
          {isCurrent ? 'Текущий' : 'Бывший'}
        </Tag>
      )
    },
    {
      title: '',
      key: 'open',
      width: 100,
      render: (_, record) =>
        record.clientId ? (
          <Button
            type="link"
            size="small"
            onClick={() => navigate(`/clients/${record.clientId}`, {
              state: fromTicketId ? { fromTicketId } : undefined
            })}
          >
            Карточка
          </Button>
        ) : null
    }
  ];

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" tip="Загрузка карточки изделия..." />
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ padding: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={goBack} style={{ marginBottom: 16 }}>
          {fromTicketId ? 'Назад в тикет' : 'Назад'}
        </Button>
        <Empty description="Изделие не найдено" />
      </div>
    );
  }

  const statusConfig = getStatusConfig(product.status);
  const photos = product.photos || [];
  const currentOwner = owners.find((o) => o.is_current) || owners[0];
  const transferCount = Math.max(0, owners.length - (owners.some((o) => o.is_current) ? 1 : 0));

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={goBack}
          style={{ marginRight: '16px' }}
        >
          {fromTicketId ? 'Назад в тикет' : 'Назад'}
        </Button>
        <Title level={2} style={{ margin: 0, display: 'inline' }}>
          {product.name || 'Изделие'}
        </Title>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Card title="Информация об изделии" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text strong>Название:</Text>
                    <br />
                    <Text style={{ fontSize: 16 }}>{product.name || '—'}</Text>
                  </div>
                  <div>
                    <Text strong>Серийный номер:</Text>
                    <br />
                    <Text code>{product.serialNumber || '—'}</Text>
                  </div>
                  <div>
                    <Text strong>Идентификационный номер:</Text>
                    <br />
                    <Text code>{product.identificationNumber || '—'}</Text>
                  </div>
                  <div>
                    <Text strong>Статус:</Text>
                    <br />
                    <Tag color={statusConfig.color} icon={statusConfig.icon}>
                      {statusConfig.text}
                    </Tag>
                  </div>
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text strong>Материал:</Text>
                    <br />
                    <Text>{product.material?.name || '—'}</Text>
                  </div>
                  <div>
                    <Text strong>Цвет:</Text>
                    <br />
                    <Text>{product.color?.name || '—'}</Text>
                  </div>
                  <div>
                    <Text strong>Размер:</Text>
                    <br />
                    <Text>{product.size?.name || '—'}</Text>
                  </div>
                  <div>
                    <Text strong>Место производства:</Text>
                    <br />
                    <Text>
                      {product.productionLocation?.fullAddress ||
                        product.productionLocation?.city ||
                        '—'}
                    </Text>
                  </div>
                  <div>
                    <Text strong>Место выпуска:</Text>
                    <br />
                    <Text>
                      {product.releaseLocation?.fullAddress ||
                        product.releaseLocation?.city ||
                        '—'}
                    </Text>
                  </div>
                </Space>
              </Col>
            </Row>
          </Card>

          <Card title="Фотографии" style={{ marginBottom: 16 }}>
            {photos.length === 0 && !product.photoUrl ? (
              <Empty description="Нет фотографий" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Row gutter={16}>
                {(photos.length
                  ? photos
                  : [{ id: 'main', photoUrl: product.photoUrl, isPrimary: true }]
                ).map((photo) => (
                  <Col xs={24} sm={12} md={8} key={photo.id} style={{ marginBottom: 16 }}>
                    <Card
                      cover={
                        <Image
                          src={photo.photoUrl}
                          alt={photo.isPrimary ? 'Основное фото' : 'Фото'}
                          style={{ height: 200, objectFit: 'cover' }}
                        />
                      }
                      size="small"
                    >
                      <Card.Meta
                        title={photo.isPrimary ? 'Основное фото' : `Фото #${photo.displayOrder || ''}`}
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Card>

          <Card title="История владельцев">
            <Table
              dataSource={owners}
              columns={ownerColumns}
              rowKey={(r) => r.id || r.clientId || r.email}
              pagination={false}
              size="small"
              locale={{ emptyText: 'Владельцев нет' }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Статистика" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Записей истории"
                  value={history.length}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Передач"
                  value={transferCount}
                  prefix={<SwapOutlined />}
                />
              </Col>
            </Row>
          </Card>

          <Card title="Текущий владелец" style={{ marginBottom: 16 }}>
            {currentOwner ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>{currentOwner.name}</Text>
                  <br />
                  <Text type="secondary">{currentOwner.email}</Text>
                </div>
                {currentOwner.clientId && (
                  <Button
                    type="primary"
                    icon={<UserOutlined />}
                    onClick={() => navigate(`/clients/${currentOwner.clientId}`, {
                      state: fromTicketId ? { fromTicketId } : undefined
                    })}
                    block
                  >
                    Карточка владельца
                  </Button>
                )}
              </Space>
            ) : (
              <Empty description="Владелец не назначен" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>

          <Card title="История активности">
            {history.length === 0 ? (
              <Empty description="Нет событий" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Timeline
                items={history.slice(0, 20).map((item) => ({
                  children: (
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{item.action}</div>
                      <div style={{ color: '#666', fontSize: 12, marginBottom: 4 }}>
                        {formatDate(item.date)} • {item.user}
                      </div>
                      <div style={{ fontSize: 12 }}>{item.description}</div>
                    </div>
                  )
                }))}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProductCard;
